import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Matches src/pages/products/ProductsIndexLayout.js — adjust if your path differs.
import { ProductsData } from "../products/ProductsIndexLayout";

/* Brand purple — used everywhere on this section instead of each
   product's individual accentColor, so the whole grid reads as one
   consistent Sagveek brand color instead of different hues. */
const BRAND = "#6c0a9f";

/* ---------------------------------------------------------------------
   Scroll-reveal hook, self-contained.
--------------------------------------------------------------------- */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

/* ---------------------------------------------------------------------
   Per-product icon mark — matched to each of the 5 real ProductsData
   slugs (rent-property-management-solution, hospital-equipment-management,
   printing-management-system, electrical-panel-management,
   learning-management-system). Previously only the first two slugs were
   special-cased and everything else fell through to one shared default,
   which is why the last 3 cards all rendered the same icon. Color stays
   the fixed brand purple, not product.accentColor.
--------------------------------------------------------------------- */
const IconMark = ({ slug, accent }) => {
  let icon;

  if (slug === "rent-property-management-solution") {
    // House / property
    icon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4 21V9.5L12 4l8 5.5V21" stroke={accent} strokeWidth="1.8" strokeLinejoin="round" />
        <rect x="9" y="13" width="6" height="8" fill={accent} opacity="0.25" />
        <rect x="6.5" y="9.5" width="3" height="3" fill={accent} opacity="0.5" />
        <rect x="14.5" y="9.5" width="3" height="3" fill={accent} opacity="0.5" />
      </svg>
    );
  } else if (slug === "hospital-equipment-management") {
    // Heartbeat / pulse
    icon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M3 12h4l2-6 3 12 2-9 1.5 3H21"
          stroke={accent}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  } else if (slug === "printing-management-system") {
    // Printer
    icon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="5" y="3" width="14" height="6" rx="1" stroke={accent} strokeWidth="1.6" />
        <rect x="3" y="9" width="18" height="8" rx="2" stroke={accent} strokeWidth="1.6" />
        <rect x="7" y="15" width="10" height="6" fill={accent} opacity="0.3" />
        <circle cx="17" cy="12" r="1" fill={accent} />
      </svg>
    );
  } else if (slug === "electrical-panel-management") {
    // Panel enclosure with a bolt — distinct from the printer mark above
    icon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="4.5" y="3" width="15" height="18" rx="2" stroke={accent} strokeWidth="1.6" />
        <path
          d="M13.2 6.5L8.7 13h3.4l-1 4.5 5-6.7h-3.4l0.5-4.3z"
          fill={accent}
          opacity="0.85"
        />
      </svg>
    );
  } else if (slug === "learning-management-system") {
    // Graduation cap — distinct icon for LMS
    icon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 4L2.5 9l9.5 5 9.5-5L12 4z" fill={accent} opacity="0.22" stroke={accent} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M6.5 11.5V16c0 1.4 2.5 3 5.5 3s5.5-1.6 5.5-3v-4.5" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 9.5V15" stroke={accent} strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  } else {
    // Fallback for any future product not yet mapped above
    icon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="5" y="3" width="14" height="6" rx="1" stroke={accent} strokeWidth="1.6" />
        <rect x="3" y="9" width="18" height="8" rx="2" stroke={accent} strokeWidth="1.6" />
        <rect x="7" y="15" width="10" height="6" fill={accent} opacity="0.3" />
        <circle cx="17" cy="12" r="1" fill={accent} />
      </svg>
    );
  }

  return (
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 14,
        background: `${accent}17`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
  );
};

/* ---------------------------------------------------------------------
   Per-product mini app preview, tucked into the top-right of each card.
   One distinct layout per real slug so all 5 cards look different —
   previously "printing-management-system", "electrical-panel-management"
   and "learning-management-system" all shared one generic preview.
   Uses the fixed brand purple wherever it previously used the varying
   per-product accent, so it matches across all cards.
--------------------------------------------------------------------- */
const ProductPreview = ({ slug, accent }) => {
  const frame = {
    width: 190,
    height: 128,
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 14px 28px rgba(15,23,42,0.14)",
    border: "1px solid #eef0f4",
    overflow: "hidden",
    padding: 10,
    fontFamily: "'Inter', sans-serif",
  };

  if (slug === "rent-property-management-solution") {
    return (
      <div style={frame}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 8, fontWeight: 700, color: "#111827" }}>Properties</span>
          <span style={{ fontSize: 6.5, fontWeight: 700, color: "#fff", background: accent, borderRadius: 8, padding: "2px 6px" }}>
            + Add
          </span>
        </div>
        <div style={{ display: "flex", gap: 5 }}>
          {["#c7e8d4", "#d6c7e8", "#c7d9e8"].map((c, i) => (
            <div key={i} style={{ flex: 1 }}>
              <div style={{ height: 34, borderRadius: 6, background: c }} />
              <div style={{ height: 5, width: "70%", background: "#e5e7eb", borderRadius: 3, marginTop: 4 }} />
              <div style={{ height: 5, width: "45%", background: accent, opacity: 0.5, borderRadius: 3, marginTop: 3 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (slug === "hospital-equipment-management") {
    return (
      <div style={frame}>
        <span style={{ fontSize: 8, fontWeight: 700, color: "#111827" }}>Equipment Overview</span>
        <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
          {[
            ["1,248", "Total"],
            ["842", "In Use"],
            ["126", "Maint."],
          ].map(([v, l]) => (
            <div key={l} style={{ flex: 1, border: "1px solid #eef0f4", borderRadius: 5, padding: "4px 2px", textAlign: "center" }}>
              <div style={{ fontSize: 8.5, fontWeight: 800, color: "#111827" }}>{v}</div>
              <div style={{ fontSize: 5.5, color: "#9ca3af", fontWeight: 600 }}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 7, display: "flex", flexDirection: "column", gap: 3 }}>
          {[0.8, 0.55, 0.3].map((w, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: accent, opacity: 0.5 + i * 0.2 }} />
              <div style={{ height: 5, width: `${w * 100}%`, background: "#e5e7eb", borderRadius: 3 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (slug === "printing-management-system") {
    return (
      <div style={frame}>
        <span style={{ fontSize: 8, fontWeight: 700, color: "#111827" }}>Production Overview</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
          <svg width="46" height="46" viewBox="0 0 46 46">
            <circle cx="23" cy="23" r="18" fill="none" stroke="#ede9fe" strokeWidth="8" />
            <circle
              cx="23"
              cy="23"
              r="18"
              fill="none"
              stroke={accent}
              strokeWidth="8"
              strokeDasharray="70 113"
              strokeLinecap="round"
              transform="rotate(-90 23 23)"
            />
            <circle cx="23" cy="23" r="18" fill="none" stroke="#a78bfa" strokeWidth="8" strokeDasharray="28 113" strokeDashoffset="-70" transform="rotate(-90 23 23)" />
          </svg>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {["Completed", "In Progress", "Pending"].map((l, i) => (
              <div key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: i === 0 ? accent : i === 1 ? "#a78bfa" : "#e9d5ff" }} />
                <span style={{ fontSize: 6, color: "#6b7280", fontWeight: 600 }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (slug === "electrical-panel-management") {
    // QR/registry-style list — distinct from the donut used by printing
    const panels = [
      { id: "PNL-1042", status: "Live" },
      { id: "PNL-1041", status: "Live" },
      { id: "PNL-1039", status: "Pending" },
    ];
    return (
      <div style={frame}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 8, fontWeight: 700, color: "#111827" }}>Panel Registry</span>
          <span style={{ fontSize: 6.5, fontWeight: 700, color: "#fff", background: accent, borderRadius: 8, padding: "2px 6px" }}>
            + Add
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {panels.map((p) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 2,
                    background: `${accent}22`,
                    border: `1px solid ${accent}55`,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridTemplateRows: "1fr 1fr",
                    gap: 1,
                    padding: 1.5,
                  }}
                >
                  <div style={{ background: accent, opacity: 0.9 }} />
                  <div style={{ background: accent, opacity: 0.3 }} />
                  <div style={{ background: accent, opacity: 0.3 }} />
                  <div style={{ background: accent, opacity: 0.9 }} />
                </div>
                <span style={{ fontSize: 6.5, fontFamily: "'JetBrains Mono', monospace", color: "#111827" }}>{p.id}</span>
              </div>
              <span
                style={{
                  fontSize: 5.5,
                  fontWeight: 700,
                  color: p.status === "Live" ? accent : "#9ca3af",
                }}
              >
                {p.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (slug === "learning-management-system") {
    // Course progress bars + certificate badge — distinct from every other card
    const courses = [
      { name: "Onboarding", pct: 82 },
      { name: "Safety Training", pct: 60 },
      { name: "Compliance", pct: 35 },
    ];
    return (
      <div style={frame}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 8, fontWeight: 700, color: "#111827" }}>My Courses</span>
          <span
            style={{
              fontSize: 8,
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: accent,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            🎓
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {courses.map((c) => (
            <div key={c.name}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ fontSize: 6, color: "#6b7280", fontWeight: 600 }}>{c.name}</span>
                <span style={{ fontSize: 6, color: accent, fontWeight: 700 }}>{c.pct}%</span>
              </div>
              <div style={{ height: 4, background: "#e5e7eb", borderRadius: 3 }}>
                <div style={{ height: 4, width: `${c.pct}%`, background: accent, borderRadius: 3 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Fallback for any future product not yet mapped above
  return (
    <div style={frame}>
      <span style={{ fontSize: 8, fontWeight: 700, color: "#111827" }}>Overview</span>
    </div>
  );
};

/* ---------------------------------------------------------------------
   Individual product card — category label, icon mark, preview accent,
   and "Learn More" link all use the fixed BRAND purple now instead of
   product.accentColor, so every card matches the header's brand color.
--------------------------------------------------------------------- */
const ProductCard = ({ product, index, onView }) => {
  const [ref, inView] = useReveal(0.2);

  return (
    <div
      ref={ref}
      className="home-product-card"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 0.6s ease ${index * 0.08}s, transform 0.6s ease ${index * 0.08}s`,
        position: "relative",
        background: "#fff",
        border: "1px solid #eef0f4",
        borderRadius: 18,
        padding: "26px 24px 28px",
        cursor: "pointer",
        overflow: "visible",
      }}
      onClick={() => onView(product.slug)}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22 }}>
        <IconMark slug={product.slug} accent={BRAND} />
        <div style={{ marginTop: -34, marginRight: -8 }}>
          <ProductPreview slug={product.slug} accent={BRAND} />
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: BRAND, marginBottom: 8 }}>
        {product.sector.toUpperCase()}
      </div>

      <h3
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 19,
          fontWeight: 800,
          color: "#111827",
          margin: "0 0 10px",
          lineHeight: 1.3,
        }}
      >
        {product.productName}
      </h3>

      <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.65, margin: "0 0 20px" }}>{product.tagline}</p>

      <button
        type="button"
        className="home-product-link"
        onClick={(e) => {
          e.stopPropagation();
          onView(product.slug);
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          padding: 0,
          fontSize: 14,
          fontWeight: 700,
          color: BRAND,
          cursor: "pointer",
        }}
      >
        Learn More <span aria-hidden="true">→</span>
      </button>
    </div>
  );
};

/* ---------------------------------------------------------------------
   Section
--------------------------------------------------------------------- */
const ProductsSection = () => {
  const navigate = useNavigate();
  const [headerRef, headerInView] = useReveal(0.3);

  const goTo = (slug) => navigate(`/products/${slug}`);

  return (
    <section
      className="home-products-section"
      style={{
        position: "relative",
        padding: "84px 24px 90px",
        overflow: "hidden",
      }}
    >

      <style>{`
        .home-products-section{
          background-color:#f4f2fb;
          background-image:url(/images/home/5.png);
          background-image:image-set(url("/images/home/5.avif") type("image/avif"), url("/images/home/5.webp") type("image/webp"), url("/images/home/5.png") type("image/png"));
          background-repeat:no-repeat;
          background-position:center center;
          background-size:cover;
        }
        @media (max-width:768px){
          .home-products-section{
            background-image:url(/images/home/5.png);
            background-image:image-set(url("/images/home/5-960.avif") type("image/avif"), url("/images/home/5-960.webp") type("image/webp"), url("/images/home/5.png") type("image/png"));
          }
        }
        .home-products-header{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:24px;
          max-width:1200px;
          margin:0 auto 52px;
          flex-wrap:wrap;
          position:relative;
          z-index:1;
        }
        .home-products-grid{
          display:grid;
          grid-template-columns:repeat(3, 1fr);
          gap:26px;
          max-width:1200px;
          margin:0 auto;
          position:relative;
          z-index:1;
        }
        .home-product-card:hover{
          border-color:#e0d9ff;
          box-shadow:0 20px 40px rgba(76,29,149,0.10);
          transform:translateY(-3px) !important;
        }
        .view-all-products-btn{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding:12px 24px;
          border-radius:30px;
          border:1.5px solid #ddd6fe;
          background:#fff;
          color:${BRAND};
          font-weight:700;
          font-size:14px;
          cursor:pointer;
          white-space:nowrap;
          margin-top:8px;
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .view-all-products-btn:hover{
          background:#f5f3ff;
          transform:translateY(-1px);
        }
        @media (max-width: 900px){
          .home-products-grid{ grid-template-columns:1fr; }
          .home-products-header{ align-items:flex-start; }
          .view-all-products-btn{ margin-top:20px; }
        }
      `}</style>

      <div
        ref={headerRef}
        className="home-products-header"
        style={{
          opacity: headerInView ? 1 : 0,
          transform: headerInView ? "translateY(0)" : "translateY(12px)",
          transition: "opacity 0.6s ease, transform 0.6s ease",
        }}
      >
        <div style={{ maxWidth: 620 }}>
          <p
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: BRAND,
              margin: "0 0 18px",
            }}
          >
            OUR PRODUCTS <span style={{ width: 6, height: 6, borderRadius: "50%", background: BRAND, display: "inline-block" }} />
          </p>
          <h2
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: "clamp(2.1rem, 4vw, 3rem)",
              fontWeight: 800,
              color: "#0f0a1f",
              margin: "0 0 20px",
              lineHeight: 1.15,
            }}
          >
            Purpose-Built Software
            <br />
            for Your Business{" "}
            <span
              style={{
                fontFamily: "'Outfit', sans-serif",
                color: BRAND,
              }}
            >
              Growth
            </span>
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", color: "#6b7280", fontSize: 15.5, lineHeight: 1.75, margin: 0, maxWidth: 480 }}>
            Powerful, scalable, and intuitive solutions designed to streamline operations and drive better results.
          </p>
        </div>

        <button type="button" className="view-all-products-btn" onClick={() => navigate("/products")}>
          View All Products <span aria-hidden="true">→</span>
        </button>
      </div>

      <div className="home-products-grid">
        {ProductsData.map((product, i) => (
          <ProductCard key={product.slug} product={product} index={i} onView={goTo} />
        ))}
      </div>
    </section>
  );
};

export default ProductsSection;